/**
 * The Add/Edit Attribute modal (piece 5; decisions 0165, 0167). One modal
 * serves both hosts: an `ItemAttribute` on an Item and a `ComponentAttribute`
 * on an `AssetNode`. The two records differ only in which parent they hang off
 * and in their column shape, and the parent is filled from the context that
 * opened the modal and locked.
 */
export type AttributeHost = 'ItemAttribute' | 'ComponentAttribute';

/** The two kinds the modal branches on; the other five `AttributeType` kinds are custom fields. */
export type CatalogAttributeKind = 'Measured' | 'Enumerated';

export interface AttributeTypeRow {
  id: string;
  attributeName: string;
  kind: CatalogAttributeKind;
  unitFamilyId: string | null;
  defaultUnitId: string | null;
}

export interface UnitRow {
  id: string;
  familyId: string;
  unitName: string;
  abbreviation: string;
  conversionFactor: number;
  conversionOffset: number;
}

export interface AttributeTypeValueRow {
  id: string;
  attributeTypeId: string;
  value: string;
}

/** A Measured type shows the value and unit fields; an Enumerated type shows a picker and no unit field at all. */
export function branchFor(kind: CatalogAttributeKind): 'measured' | 'enumerated' {
  return kind === 'Measured' ? 'measured' : 'enumerated';
}

/**
 * The unit family is derived from `AttributeType.unitFamilyId` and never
 * asked: choosing Motor Rating restricts the unit list to Power. This is the
 * deliberate opposite of `Item.unitId`, which is asked plainly because nothing
 * upstream exists to derive it from.
 */
export function unitChoices(units: readonly UnitRow[], type: AttributeTypeRow): UnitRow[] {
  if (type.kind !== 'Measured' || !type.unitFamilyId) return [];
  return units.filter((u) => u.familyId === type.unitFamilyId);
}

/** Unit defaults to `AttributeType.defaultUnitId`, so Motor Rating opens on MechanicalHorsepower. */
export function defaultUnitId(type: AttributeTypeRow): string | null {
  return type.kind === 'Measured' ? type.defaultUnitId : null;
}

/** The number inside a typed value, or null where there is not one. */
export function numericFrom(entered: string): number | null {
  const match = /-?\d+(\.\d+)?/.exec(entered.trim());
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
}

/**
 * `baseValue` is never a form field; the modal computes it on save as the
 * entered number times the unit's factor plus its offset, against the family's
 * base unit. A field for it would be a second source of truth for a number the
 * system derives.
 */
export function computeBaseValue(entered: number | null, unit: Pick<UnitRow, 'conversionFactor' | 'conversionOffset'> | null): number | null {
  if (entered === null || !unit) return null;
  return entered * unit.conversionFactor + unit.conversionOffset;
}

/**
 * Changing the unit reinterprets the entered value; it does not convert it.
 * An attribute always displays in its default unit, so the unit control exists
 * only to correct a wrongly entered unit — converting instead would make
 * correcting a mistake impossible without arithmetic.
 */
export function reinterpretOnUnitChange(entered: string, nextUnit: UnitRow): { enteredValue: string; baseValue: number | null } {
  return { enteredValue: entered, baseValue: computeBaseValue(numericFrom(entered), nextUnit) };
}

export const COMPONENT_VISIBILITIES = ['Asset record', 'Work Orders', 'Reports'] as const;
export type ComponentVisibility = (typeof COMPONENT_VISIBILITIES)[number];
export const DEFAULT_COMPONENT_VISIBILITY: ComponentVisibility = 'Asset record';

/** Notes and Visibility are `AssetNode` columns; `ItemAttribute` carries neither. */
export function showsNotesAndVisibility(host: AttributeHost): boolean {
  return host === 'ComponentAttribute';
}

export interface AttributeDraft {
  host: AttributeHost;
  /** Filled from the opening context and locked, so no run repoints the attribute at another host. */
  parentId: string;
  attributeTypeId: string | null;
  /** The value exactly as typed, and what the modal shows when it reopens. */
  enteredValue: string;
  unitId: string | null;
  attributeTypeValueId: string | null;
  notes: string | null;
  visibility: ComponentVisibility;
}

export function emptyAttribute(host: AttributeHost, parentId: string): AttributeDraft {
  return {
    host,
    parentId,
    attributeTypeId: null,
    enteredValue: '',
    unitId: null,
    attributeTypeValueId: null,
    notes: null,
    visibility: DEFAULT_COMPONENT_VISIBILITY,
  };
}

/** An `ItemAttribute` row: the number as typed, and the computed base value. */
export interface ItemAttributeWrite {
  host: 'ItemAttribute';
  itemId: string;
  attributeTypeId: string;
  enteredValue: number | null;
  baseValue: number | null;
  unitId: string | null;
  attributeTypeValueId: string | null;
}

/** A `ComponentAttribute` row: the string as typed, the number in it, and the base value from that number. */
export interface ComponentAttributeWrite {
  host: 'ComponentAttribute';
  assetNodeId: string;
  attributeTypeId: string;
  enteredValue: string;
  numericValue: number | null;
  baseValue: number | null;
  unitId: string | null;
  attributeTypeValueId: string | null;
  notes: string | null;
  visibility: ComponentVisibility;
}

export type AttributeWrite = ItemAttributeWrite | ComponentAttributeWrite;

export class AttributeModalError extends Error {}

/**
 * On the Measured branch the modal writes each host's own columns, because the
 * two hosts do not share a column shape. On the Enumerated branch it writes
 * the chosen `AttributeTypeValue` and no unit: free text on an enumerated
 * rating puts ip20, IP-20 and IP 20 into three part numbers.
 */
export function attributeWrite(draft: AttributeDraft, type: AttributeTypeRow, unit: UnitRow | null): AttributeWrite {
  if (!draft.attributeTypeId) throw new AttributeModalError('an attribute value names its AttributeType before it saves (piece 5)');
  const enumerated = branchFor(type.kind) === 'enumerated';
  if (enumerated && !draft.attributeTypeValueId) {
    throw new AttributeModalError(`${type.attributeName} is Enumerated and takes one of its own AttributeTypeValue rows, never a typed value (piece 5)`);
  }
  const numeric = enumerated ? null : numericFrom(draft.enteredValue);
  const base = enumerated ? null : computeBaseValue(numeric, unit);
  const unitId = enumerated ? null : (unit?.id ?? null);
  if (draft.host === 'ItemAttribute') {
    return {
      host: 'ItemAttribute',
      itemId: draft.parentId,
      attributeTypeId: draft.attributeTypeId,
      enteredValue: numeric,
      baseValue: base,
      unitId,
      attributeTypeValueId: enumerated ? draft.attributeTypeValueId : null,
    };
  }
  return {
    host: 'ComponentAttribute',
    assetNodeId: draft.parentId,
    attributeTypeId: draft.attributeTypeId,
    enteredValue: draft.enteredValue,
    numericValue: numeric,
    baseValue: base,
    unitId,
    attributeTypeValueId: enumerated ? draft.attributeTypeValueId : null,
    notes: draft.notes,
    visibility: draft.visibility,
  };
}

/**
 * Reopening shows the originally typed value, not the record page's display
 * value: a value entered as `3.7 kW` displays as `5 HP` on the page while the
 * modal reopens on `3.7 kW`. The modal is the only place the typed value is
 * visible, and that is what makes editing possible at all.
 */
export function reopenValue(write: AttributeWrite): string {
  return write.host === 'ComponentAttribute' ? write.enteredValue : write.enteredValue === null ? '' : String(write.enteredValue);
}
